import type { MenuItem } from "@/types/Sidebar.type";

/**
 * Paths always reachable from chrome (Header) even when not in the sidebar menu.
 */
export const ALWAYS_ALLOWED_PATHS = new Set<string>([
  "/",
  "/login",
  "/profile",
  "/settings",
  "/settings/security-policy",
  /** Universal home — RoleDashboard; always reachable after login */
  "/dashboard",
  /** CTA menu home redirects here via role section */
  "/cta/dashboard",
]);

/**
 * If the user can open any path in a group, sibling/detail URLs in that group
 * are also allowed (e.g. visit detail after opening Monitor Tracker).
 *
 * Groups align API/menu prefixes with in-app role-prefixed navigation without
 * changing menu-configurations.json paths.
 */
export const MENU_ACCESS_EQUIVALENCE_GROUPS: readonly (readonly string[])[] = [
  [
    "/monitor-tracker",
    "/cra/monitor-tracker",
    "/pm/monitor-tracker",
    "/admin/monitoring-tracker",
    "/monitoring-visits",
  ],
  /** Tenant Admin API menu uses /admin/audit-logs; short /audit-logs is equivalent */
  ["/audit-logs", "/admin/audit-logs"],
  [
    "/regulatory",
    "/cra/regulatory",
    "/ctms/regulatory",
    "/sc/regulatory",
    "/pi/regulatory",
    "/pm/regulatory",
    "/dm/regulatory",
    "/sponsor/regulatory",
    "/tenant/regulatory",
  ],
  [
    "/study-dashboard",
    "/ctms/study-dashboard",
    "/cra/study-dashboard",
    "/sc/study-dashboard",
    "/dm/study-dashboard",
    "/pi/study-dashboard",
    "/tenant/study-dashboard",
  ],
  [
    "/site-dashboard",
    "/ctms/site-dashboard",
    "/cra/site-dashboard",
    "/tenant/site-dashboard",
  ],
  [
    "/etmf/document-library",
    "/etmf/completeness-dashboard",
    "/etmf/retention-archival",
    "/etmf/inspection-mode",
  ],
  [
    "/ctms/site-feasibility",
    "/cra/site-feasibility",
    "/cta/site-feasibility",
    "/sponsor/site-feasibility",
    "/pm/site-feasibility",
    "/dm/site-feasibility",
  ],
  [
    "/ctms/site-activation",
    "/cra/site-activation",
    "/sponsor/site-activation",
    "/pm/site-activation",
    "/cta/site-activation",
  ],
  [
    "/risk-tracker",
    "/cra/risk-tracker",
    "/sc/risk-tracker",
    "/pm/risk-tracker",
    "/dm/risk-tracker",
    "/pi/risk-tracker",
    "/sponsor/risk-tracker",
    "/ctms/risk-tracker",
  ],
  [
    "/cra/rbqm-center",
    "/sc/rbqm-center",
    "/pm/rbqm-center",
    "/dm/rbqm-center",
    "/pi/rbqm-center",
    "/sponsor/rbqm-center",
    "/cta/rbqm-center",
    "/ctms/rbqm-center",
  ],
  ["/cra/tasks", "/sc/tasks"],
  ["/sponsor/analytics/reports", "/ctms-reports"],
];

/** Collect active leaf/item paths from the live sidebar menu. */
export function collectActiveMenuPaths(items: MenuItem[]): Set<string> {
  const paths = new Set<string>();

  const walk = (nodes: MenuItem[]) => {
    for (const item of nodes) {
      if (item.children?.length) {
        walk(item.children);
      }
      const path = item.path?.trim();
      if (!path || path === "") {
        continue;
      }
      if (item.active === false) {
        continue;
      }
      paths.add(path);
    }
  };

  walk(items);
  return paths;
}

function expandWithEquivalence(menuPaths: Set<string>): Set<string> {
  const expanded = new Set(menuPaths);
  for (const group of MENU_ACCESS_EQUIVALENCE_GROUPS) {
    if (group.some((p) => menuPaths.has(p))) {
      for (const p of group) {
        expanded.add(p);
      }
    }
  }
  return expanded;
}

/**
 * True when `pathname` is covered by an allowlisted menu path
 * (exact match or nested detail under that path).
 */
export function pathMatchesAllowlist(
  pathname: string,
  allowlist: Set<string>,
): boolean {
  const path = pathname.split("?")[0] ?? "/";
  if (allowlist.has(path)) {
    return true;
  }

  for (const allowed of allowlist) {
    if (allowed !== "/" && path.startsWith(`${allowed}/`)) {
      return true;
    }
  }
  return false;
}

/**
 * Strict menu-based page access: only sidebar (or always-allowed) paths.
 * Uses the live menu the user sees — not a static role map.
 *
 * Fail-open when the menu has no active paths yet (bootstrap / fetch race)
 * so login and Tenant Admin navigation are not blocked on an empty allowlist.
 */
export function isPathAllowedByMenu(
  pathname: string,
  menuItems: MenuItem[],
): boolean {
  const path = pathname.split("?")[0] ?? "/";
  if (ALWAYS_ALLOWED_PATHS.has(path)) {
    return true;
  }
  if (path.startsWith("/settings/")) {
    return true;
  }
  if (path.startsWith("/dashboard/")) {
    return true;
  }

  const menuPaths = expandWithEquivalence(collectActiveMenuPaths(menuItems));
  if (menuPaths.size === 0) {
    return true;
  }
  return pathMatchesAllowlist(path, menuPaths);
}
